/* AutoPrintSettings — Bol siparişleri için otomatik etiket basımı yapılandırması.
 * Webhook ile gelen yeni siparişlerde Bol + Bpost + Pick Slip otomatik PrintNode'a gönderilir.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Printer, Zap, ZapOff, AlertTriangle, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const HDR = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function AutoPrintSettings() {
    const [settings, setSettings] = useState(null);
    const [stats, setStats] = useState(null);
    const [printers, setPrinters] = useState([]);
    const [loadingPrinters, setLoadingPrinters] = useState(false);
    const [saving, setSaving] = useState(false);

    const loadAll = async () => {
        try {
            const [s, st] = await Promise.all([
                axios.get(`${API}/api/bol/auto-print/settings`, { headers: HDR() }),
                axios.get(`${API}/api/bol/auto-print/stats`, { headers: HDR() }),
            ]);
            setSettings(s.data);
            setStats(st.data);
        } catch { /* sessiz */ }
    };

    const loadPrinters = async () => {
        setLoadingPrinters(true);
        try {
            const r = await axios.get(`${API}/api/printnode/printers`, { headers: HDR() });
            setPrinters(r.data?.printers || []);
        } catch (e) {
            if (e.response?.status === 400) {
                toast.error('Önce PrintNode yapılandırmalısınız (aşağıdaki kart)');
            }
        } finally {
            setLoadingPrinters(false);
        }
    };

    useEffect(() => { loadAll(); loadPrinters(); }, []);

    // Iter 113.42: testPulse kaldırıldı (endpoint artık yok).

    const save = async (patch) => {
        setSaving(true);
        try {
            const r = await axios.post(`${API}/api/bol/auto-print/settings`,
                { ...settings, ...patch }, { headers: HDR() });
            setSettings(r.data);
            await loadAll();
            toast.success('Kaydedildi');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Hata');
        } finally {
            setSaving(false);
        }
    };

    if (!settings) return (
        <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-2">
            <Loader2 size={14} className="animate-spin text-gray-400" />
            <span className="text-xs text-gray-500">Yükleniyor...</span>
        </div>
    );

    const isEnabled = settings.enabled;
    const printerOk = !!settings.printer_id;

    return (
        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-4" data-testid="auto-print-settings">
            <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg ${isEnabled ? 'bg-emerald-100' : 'bg-gray-100'}`}>
                    {isEnabled ? <Zap size={18} className="text-emerald-700" /> : <ZapOff size={18} className="text-gray-500" />}
                </div>
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <h3 className="text-sm font-bold text-gray-900">Otomatik Etiket Basımı</h3>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${isEnabled ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                            {isEnabled ? 'AKTİF' : 'KAPALI'}
                        </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                        Yeni sipariş geldiğinde Bol etiketi + Bpost (varsa) + Pack Slip otomatik bastırılır.
                        7/24 çalışır, idempotent (aynı sipariş 2 kez basılmaz).
                    </p>
                </div>
                <button
                    onClick={() => save({ enabled: !isEnabled })}
                    disabled={saving || (!isEnabled && !printerOk)}
                    className={`h-10 px-4 text-sm font-bold rounded-lg disabled:opacity-50 ${
                        isEnabled
                            ? 'bg-rose-600 hover:bg-rose-700 text-white'
                            : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                    }`}
                    data-testid="auto-print-toggle">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : isEnabled ? 'Kapat' : 'Aç'}
                </button>
            </div>

            {!printerOk && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-2.5 text-xs text-amber-900 flex items-start gap-2">
                    <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                    <div>Açmadan önce <strong>yazıcı seçmelisiniz</strong>. Aşağıdaki PrintNode kartından önce API key tanımlı olmalı.</div>
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <label className="text-[10px] font-bold uppercase text-gray-500 block mb-1">Yazıcı</label>
                    <div className="flex gap-2">
                        <select
                            value={settings.printer_id || ''}
                            onChange={(e) => save({ printer_id: e.target.value ? parseInt(e.target.value) : null })}
                            disabled={saving || loadingPrinters}
                            className="flex-1 h-10 px-2 text-sm border border-gray-300 rounded-md"
                            data-testid="auto-print-printer-select">
                            <option value="">— Yazıcı seç —</option>
                            {printers.map((p) => (
                                <option key={p.id} value={p.id}>{p.name}{p.computer?.name ? ` (${p.computer.name})` : ''}</option>
                            ))}
                        </select>
                        <button onClick={loadPrinters} disabled={loadingPrinters}
                            className="h-10 px-2 text-xs font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded inline-flex items-center gap-1"
                            title="Yazıcı listesini yenile">
                            {loadingPrinters ? <Loader2 size={12} className="animate-spin" /> : <Printer size={12} />}
                        </button>
                    </div>
                </div>
                <div>
                    <label className="text-[10px] font-bold uppercase text-gray-500 block mb-1">Kopya Sayısı</label>
                    <input
                        type="number" min={1} max={5}
                        value={settings.copies || 1}
                        onChange={(e) => setSettings({ ...settings, copies: parseInt(e.target.value) || 1 })}
                        onBlur={(e) => save({ copies: parseInt(e.target.value) || 1 })}
                        className="w-full h-10 px-3 text-sm border border-gray-300 rounded-md"
                        data-testid="auto-print-copies" />
                </div>
            </div>

            {stats && (
                <div className="border-t pt-3 grid grid-cols-3 gap-2 text-center">
                    <Stat label="Bugün Basılan" value={stats.today_printed || 0} color="text-emerald-700" icon={<CheckCircle2 size={11} />} />
                    <Stat label="Hata" value={stats.stats_today?.errors || 0} color="text-rose-700" icon={<AlertTriangle size={11} />} />
                    <Stat label="Son Basım"
                        value={stats.last_print_at ? new Date(stats.last_print_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : '—'}
                        color="text-gray-700" icon={<Printer size={11} />} />
                </div>
            )}

            {/* Iter 113.47: use_driver_defaults ve first_page_only bölümleri kaldırıldı.
                Yeni print sistemi (content bbox + native placement + auto-rotate + no-scale)
                bu ayarlara ihtiyaç duymuyor. fit_to_page HARD invariant false.
                use_driver_defaults=true → PrintNode'a hiç option gönderme, kayıtta duruyor
                ancak artık kullanılmıyor. */}

            {/* Iter 113.42: TPCL bölümü kaldırıldı. Yerine no-scale placement kontrolleri. */}
            <div className="border-t pt-3 mt-3">
                <div className="text-xs font-bold text-gray-800 mb-2">
                    Yerleşim & Ofset <span className="text-gray-500 font-normal">(Toshiba B-EX4T1 için no-scale)</span>
                </div>
                <div className="grid grid-cols-4 gap-2">
                    <div>
                        <div className="text-[10px] font-semibold text-gray-600 uppercase mb-1">Döndür (°)</div>
                        <div className="flex gap-1">
                            {[0, 90, 180, 270].map((deg) => (
                                <button
                                    key={deg}
                                    type="button"
                                    onClick={() => save({ rotate_deg: deg })}
                                    disabled={saving}
                                    data-testid={`auto-print-rotate-${deg}`}
                                    className={`flex-1 text-[10px] px-1 py-1 rounded border ${
                                        (settings.rotate_deg ?? 0) === deg
                                            ? 'bg-gray-800 text-white border-gray-800'
                                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'
                                    }`}
                                >
                                    {deg}
                                </button>
                            ))}
                        </div>
                    </div>
                    <NumField
                        label="X Ofset (mm)"
                        value={settings.x_offset_mm ?? 0}
                        step={0.5}
                        min={-50}
                        max={50}
                        onChange={(v) => save({ x_offset_mm: v })}
                        testId="auto-print-x-offset"
                        disabled={saving}
                    />
                    <NumField
                        label="Y Ofset (mm)"
                        value={settings.y_offset_mm ?? 0}
                        step={0.5}
                        min={-50}
                        max={50}
                        onChange={(v) => save({ y_offset_mm: v })}
                        testId="auto-print-y-offset"
                        disabled={saving}
                    />
                    <div>
                        <div className="text-[10px] font-semibold text-gray-600 uppercase mb-1">DPI</div>
                        <input
                            type="text"
                            value={settings.dpi ?? '203x203'}
                            onChange={(e) => save({ dpi: e.target.value })}
                            disabled={saving}
                            data-testid="auto-print-dpi"
                            className="w-full text-xs px-2 py-1 border border-gray-300 rounded"
                            placeholder="203x203"
                        />
                    </div>
                </div>
                <div className="text-[10px] text-gray-500 mt-2 italic">
                    Yerleşim <strong>native size</strong>&apos;da yapılır (scale yok). Barcode dot boundary korunur.
                    Kaynak {'>'} 104mm ise print head limitini aşar — bu durumda döndürerek deneyin.
                </div>

                {/* Iter 113.43: Opsiyonel hedef sayfa boyut override */}
                <div className="mt-3 border-t pt-3">
                    <div className="text-[10px] font-semibold text-gray-600 uppercase mb-1">
                        Etiket Boyut Override (opsiyonel)
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <NumField
                            label="Genişlik (mm)"
                            value={settings.label_w_mm_override ?? ''}
                            step={0.1}
                            min={10}
                            max={300}
                            onChange={(v) => save({ label_w_mm_override: v || null })}
                            testId="auto-print-label-w-override"
                            disabled={saving}
                        />
                        <NumField
                            label="Yükseklik (mm)"
                            value={settings.label_h_mm_override ?? ''}
                            step={0.1}
                            min={10}
                            max={300}
                            onChange={(v) => save({ label_h_mm_override: v || null })}
                            testId="auto-print-label-h-override"
                            disabled={saving}
                        />
                    </div>
                    <div className="text-[10px] text-gray-500 mt-1 italic">
                        Boş bırakılırsa PrintNode&apos;dan seçili paper boyutu kullanılır. Farklı rulo kullanıyorsanız buraya yazın.
                    </div>
                </div>
            </div>

            {stats?.stats_today?.last_error && (
                <div className="bg-rose-50 border border-rose-200 rounded p-2 text-[11px] text-rose-800">
                    <strong>Son hata:</strong> {stats.stats_today.last_error}
                </div>
            )}
        </div>
    );
}

function Stat({ label, value, color, icon }) {
    return (
        <div className="bg-gray-50 border border-gray-200 rounded-md p-2">
            <div className={`text-xl font-bold ${color}`}>{value}</div>
            <div className="text-[10px] uppercase font-bold text-gray-500 mt-0.5 flex items-center justify-center gap-1">
                {icon} {label}
            </div>
        </div>
    );
}

function NumField({ label, value, step, min, max, onChange, testId, disabled }) {
    const [local, setLocal] = useState(String(value ?? ''));
    useEffect(() => { setLocal(String(value ?? '')); }, [value]);
    const commit = () => {
        const n = parseFloat(local);
        if (Number.isFinite(n) && n !== Number(value)) {
            const clamped = Math.min(max ?? Infinity, Math.max(min ?? -Infinity, n));
            onChange(clamped);
        } else {
            setLocal(String(value ?? ''));
        }
    };
    return (
        <div>
            <div className="text-[10px] font-semibold text-amber-900 uppercase mb-1">{label}</div>
            <input
                type="number"
                step={step}
                min={min}
                max={max}
                value={local}
                disabled={disabled}
                onChange={(e) => setLocal(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                data-testid={testId}
                className="w-full text-xs px-2 py-1 border border-amber-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-amber-300"
            />
        </div>
    );
}
